import React, { useMemo, useState } from "react";
import {
  Package,
  Menu,
  X,
  ChevronLeft,
  Users,
  MessageCircleQuestion,
  Building2,
  Car,
  Truck,
  MapPin,
  UserCheck,
  LayoutDashboard,
  Settings,
  ChevronUp,
  ChevronDown,
  Calendar,
  Layers,
  CheckSquare,
  Briefcase,
  Home,
  FileText,
  SlidersHorizontal,
  ClipboardList,
  Container,
  Plug,
  Coins,
  BarChart3,
  Inbox,
  type LucideIcon,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useModules } from "../../hooks/useModules";
import { useIntakeCounts } from "../../hooks/useIntakeCounts";
import { useTranslation } from "react-i18next";
import type { ModuleKey } from "../../constants/modules";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

/**
 * One entry in the navigation.
 *
 * `module` is what ties the nav to everything else: the same key gates the
 * route (routes/index.tsx) and the API (backend/middlewares/moduleGuard.js).
 * Describing the nav as data rather than as markup is what makes that
 * enforceable — a new entry cannot be added without naming its module, so the
 * menu can never offer a door the router or the API will refuse to open.
 */
interface NavItem {
  module: ModuleKey;
  path: string;
  /** First path segment, for the active-state highlight. */
  page: string;
  icon: LucideIcon;
  /** Key under the `navigation` i18n namespace. */
  labelKey: string;
  /** A live count shown beside the label — see `badgeFor` in the component. */
  badge?: "orderIntake";
}

// Operations, in the order a day moves through them: the orders come in, they
// get planned, they get executed, they get billed.
const OPERATIONS_ITEMS: NavItem[] = [
  {
    module: "shipments",
    path: "/shipments",
    page: "shipments",
    icon: Package,
    labelKey: "items.shipments",
  },
  {
    module: "shipmentGroups",
    path: "/shipments-group",
    page: "shipments-group",
    icon: Layers,
    labelKey: "items.shipmentsGroup",
  },
  {
    module: "containerOrders",
    path: "/container-orders",
    page: "container-orders",
    icon: Container,
    labelKey: "items.containerOrders",
  },
  {
    // Oliver orders waiting to be reviewed and turned into container
    // orders. Its own top-level path so it highlights on its own.
    module: "containerOrders",
    path: "/order-intake",
    page: "order-intake",
    icon: Inbox,
    labelKey: "items.orderIntake",
    badge: "orderIntake",
  },
  {
    module: "containerOrders",
    path: "/container-reports",
    page: "container-reports",
    icon: BarChart3,
    labelKey: "items.truckReport",
  },
  {
    module: "orders",
    path: "/orders",
    page: "orders",
    icon: ClipboardList,
    labelKey: "items.orders",
  },
  {
    module: "calendar",
    path: "/operations-calendar",
    page: "operations-calendar",
    icon: Calendar,
    labelKey: "items.operationsCalendar",
  },
  {
    module: "works",
    path: "/works",
    page: "works",
    icon: Briefcase,
    labelKey: "items.works",
  },
  {
    module: "tasks",
    path: "/tasks",
    page: "tasks",
    icon: CheckSquare,
    labelKey: "items.tasks",
  },
  {
    module: "invoices",
    path: "/invoices",
    page: "invoices",
    icon: FileText,
    labelKey: "items.invoices",
  },
];

// The reference data operations are built out of.
const MANAGEMENT_ITEMS: NavItem[] = [
  {
    module: "addresses",
    path: "/addresses",
    page: "addresses",
    icon: MapPin,
    labelKey: "items.addresses",
  },
  {
    module: "partners",
    path: "/partners",
    page: "partners",
    icon: Building2,
    labelKey: "items.partners",
  },
  {
    module: "services",
    path: "/services",
    page: "services",
    icon: Settings,
    labelKey: "items.services",
  },
  {
    module: "costServices",
    path: "/cost-services",
    page: "cost-services",
    icon: Coins,
    labelKey: "items.costServices",
  },
  {
    module: "vehicles",
    path: "/vehicles",
    page: "vehicles",
    icon: Car,
    labelKey: "items.vehicles",
  },
  {
    module: "vehicles",
    path: "/truck-groups",
    page: "truck-groups",
    icon: Truck,
    labelKey: "items.truckGroups",
  },
];

// Admin-only. Who is in the tenant, and how the tenant is configured.
const USER_MANAGEMENT_ITEMS: NavItem[] = [
  {
    module: "users",
    path: "/users",
    page: "users",
    icon: Users,
    labelKey: "items.users",
  },
  {
    module: "drivers",
    path: "/drivers",
    page: "drivers",
    icon: UserCheck,
    labelKey: "items.drivers",
  },
  {
    module: "organisationSettings",
    path: "/organisation-settings",
    page: "organisation-settings",
    icon: SlidersHorizontal,
    labelKey: "items.organisationSettings",
  },
  {
    module: "integrations",
    path: "/integrations",
    page: "integrations",
    icon: Plug,
    labelKey: "items.integrations",
  },
];

// The driver app. Separate modules from their office counterparts, so a tenant
// can run container operations from the office without opening the driver app.
const DRIVER_ITEMS: NavItem[] = [
  {
    module: "driverContainers",
    path: "/driver/containers",
    page: "driver",
    icon: Container,
    labelKey: "items.containerOrders",
  },
  {
    module: "driverWorks",
    path: "/works",
    page: "works",
    icon: Briefcase,
    labelKey: "items.works",
  },
  {
    module: "driverShipments",
    path: "/shipments",
    page: "shipments",
    icon: Package,
    labelKey: "items.shipments",
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobile,
}) => {
  const location = useLocation();
  const currentPage = location.pathname.split("/")[1];
  const { user } = useAuth();
  const { has } = useModules();
  const navigate = useNavigate();
  const [isManagementOpen, setIsManagementOpen] = useState(true);
  const { t } = useTranslation("navigation");

  const isDriver = user?.role === "driver";
  const isAdmin = user?.role === "admin";

  const { pending: pendingIntakes } = useIntakeCounts(
    !!user && !isDriver && has("containerOrders"),
  );
  const badgeFor = (badge?: NavItem["badge"]) =>
    badge === "orderIntake" ? pendingIntakes : 0;

  // Drop every entry whose module this tenant does not have. Filtering up front
  // rather than per item lets each section also ask "did anything survive?" and
  // skip its own heading, so a tenant never sees a title over an empty list.
  const operationsItems = useMemo(
    () => OPERATIONS_ITEMS.filter((item) => has(item.module)),
    [has],
  );
  const managementItems = useMemo(
    () => MANAGEMENT_ITEMS.filter((item) => has(item.module)),
    [has],
  );
  const userManagementItems = useMemo(
    () => (isAdmin ? USER_MANAGEMENT_ITEMS.filter((i) => has(i.module)) : []),
    [has, isAdmin],
  );
  const driverItems = useMemo(
    () => DRIVER_ITEMS.filter((item) => has(item.module)),
    [has],
  );

  const handleNavigation = (path: string) => {
    navigate(path);

    if (isMobile && !isCollapsed) {
      onToggle();
    }
  };

  const handleOverlayClick = () => {
    if (isMobile && !isCollapsed) {
      onToggle();
    }
  };

  const companyName =
    user?.companyId && typeof user.companyId === "object"
      ? user.companyId.name
      : t("defaultOrganization");

  // Brand SVG mark
  const BrandMark = ({ size = 28 }: { size?: number }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5" />
      <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6" />
      <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7" />
      <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85" />
      <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
      <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85" />
      <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7" />
      <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6" />
      <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5" />
    </svg>
  );

  /**
   * One nav button, in both its expanded and collapsed forms.
   *
   * Every entry rendered exactly the same markup before this existed, copied
   * per item — which is how the disabled modules ended up as large commented
   * blocks rather than a flag. With the markup in one place, switching a module
   * on or off is a data change.
   */
  const NavButton: React.FC<{
    icon: LucideIcon;
    label: string;
    page: string;
    onClick: () => void;
    badge?: number;
  }> = ({ icon: Icon, label, page, onClick, badge = 0 }) => {
    const isActive = currentPage === page;

    return (
      <button
        onClick={onClick}
        className={`
          flex items-center rounded-lg font-normal w-full text-left
          transition-all duration-200 group relative
          ${
            isCollapsed && !isMobile
              ? "justify-center p-3"
              : "space-x-3 px-3 py-2"
          }
          ${
            isActive
              ? "text-[#E89D5C] bg-white/8"
              : "text-[#8A9590] hover:text-[#E89D5C] hover:bg-white/5"
          }
        `}
        role="menuitem"
        aria-current={isActive ? "page" : undefined}
        title={isCollapsed && !isMobile ? label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />

        {(!isCollapsed || isMobile) && (
          <span className="whitespace-nowrap">{label}</span>
        )}

        {badge > 0 &&
          (!isCollapsed || isMobile ? (
            <span className="ml-auto min-w-[20px] rounded-full bg-[#C97A3C] px-1.5 text-center text-[11px] font-semibold leading-5 text-white">
              {badge > 99 ? "99+" : badge}
            </span>
          ) : (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#C97A3C]" />
          ))}

        {isCollapsed && !isMobile && (
          <div
            className="
            absolute left-full ml-2 px-2 py-1 bg-[#0B0F0E] border border-white/10 text-white text-sm rounded
            opacity-0 group-hover:opacity-100 transition-opacity duration-200
            pointer-events-none whitespace-nowrap z-50
          "
          >
            {label}
          </div>
        )}
      </button>
    );
  };

  const renderItems = (items: NavItem[]) =>
    items.map((item) => (
      <li role="none" key={`${item.module}:${item.path}`}>
        <NavButton
          icon={item.icon}
          label={t(item.labelKey)}
          page={item.page}
          onClick={() => handleNavigation(item.path)}
          badge={badgeFor(item.badge)}
        />
      </li>
    ));

  const SectionHeading: React.FC<{ label: string }> = ({ label }) =>
    !isCollapsed || isMobile ? (
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-[#8A9590] uppercase tracking-wider px-3">
          {label}
        </h3>
      </div>
    ) : null;

  if (isDriver) {
    return (
      <>
        {isMobile && !isCollapsed && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={handleOverlayClick}
            aria-hidden="true"
          />
        )}

        <div
          className={`
          fixed lg:relative top-0 left-0 h-full bg-[#0B0F0E] border-r border-white/8 z-50
          flex flex-col
          transition-all duration-300 ease-in-out
          ${
            isMobile
              ? isCollapsed
                ? "-translate-x-full"
                : "translate-x-0 w-64"
              : isCollapsed
                ? "w-16"
                : "w-64"
          }
        `}
          role="navigation"
          aria-label="Main navigation"
        >
          {(!isCollapsed || isMobile) && (
            <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-3">
                <BrandMark size={28} />
                <div>
                  <div className="font-medium tracking-tight text-[#E89D5C]">
                    LiveCargo<span className="text-[#C97A3C]">.</span>
                  </div>
                  <div className="text-xs text-[#8A9590]">{companyName}</div>
                </div>
              </div>

              <button
                onClick={onToggle}
                className="p-2 rounded-lg hover:bg-white/8 transition-colors"
                aria-label={
                  isCollapsed
                    ? t("sidebar.expandSidebar")
                    : t("sidebar.collapseSidebar")
                }
                title={
                  isCollapsed
                    ? t("sidebar.expandSidebar")
                    : t("sidebar.collapseSidebar")
                }
              >
                {isMobile ? (
                  <X className="h-5 w-5 text-[#8A9590]" />
                ) : (
                  <ChevronLeft className="h-5 w-5 text-[#8A9590]" />
                )}
              </button>
            </div>
          )}

          {isCollapsed && !isMobile && (
            <div className="p-4 border-b border-white/8 flex justify-center shrink-0">
              <button
                onClick={onToggle}
                className="p-2 rounded-lg hover:bg-white/8 transition-colors"
                aria-label={t("sidebar.expandSidebar")}
                title={t("sidebar.expandSidebar")}
              >
                <Menu className="h-5 w-5 text-[#8A9590]" />
              </button>
            </div>
          )}

          <nav
            className={`${isCollapsed && !isMobile ? "p-2" : "p-4"}`}
            role="menubar"
          >
            <ul className="space-y-1" role="none">
              {/* Home is not a module — it is the driver's landing screen and
                  where every gated driver route redirects to, so it is always
                  reachable even when this tenant has no driver modules at all. */}
              <li role="none">
                <NavButton
                  icon={Home}
                  label={t("items.home")}
                  page="driver-home"
                  onClick={() => handleNavigation("/driver-home")}
                />
              </li>
              {renderItems(driverItems)}
            </ul>
          </nav>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && !isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      <div
        className={`
          fixed lg:relative top-0 left-0 h-full bg-[#0B0F0E] border-r border-white/8 z-50
          flex flex-col
          transition-all duration-300 ease-in-out
          ${
            isMobile
              ? isCollapsed
                ? "-translate-x-full"
                : "translate-x-0 w-64"
              : isCollapsed
                ? "w-16"
                : "w-64"
          }
        `}
        role="navigation"
        aria-label="Main navigation"
      >
        {(!isCollapsed || isMobile) && (
          <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between shrink-0">
            <div className="flex items-center space-x-3">
              <BrandMark size={28} />
              <div>
                <div className="font-medium tracking-tight text-[#E89D5C]">
                  LiveCargo<span className="text-[#C97A3C]">.</span>
                </div>
                <div className="text-xs text-[#8A9590]">{companyName}</div>
              </div>
            </div>

            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              aria-label={
                isCollapsed
                  ? t("sidebar.expandSidebar")
                  : t("sidebar.collapseSidebar")
              }
              title={
                isCollapsed
                  ? t("sidebar.expandSidebar")
                  : t("sidebar.collapseSidebar")
              }
            >
              {isMobile ? (
                <X className="h-5 w-5 text-[#8A9590]" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-[#8A9590]" />
              )}
            </button>
          </div>
        )}

        {isCollapsed && !isMobile && (
          <div className="py-4 px-2 border-b border-white/8 flex flex-col items-center space-y-2 shrink-0">
            <BrandMark size={28} />
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              aria-label={t("sidebar.expandSidebar")}
              title={t("sidebar.expandSidebar")}
            >
              <Menu className="h-5 w-5 text-[#8A9590]" />
            </button>
          </div>
        )}

        <nav
          className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden sidebar-scroll ${
            isCollapsed && !isMobile ? "p-2" : "p-4"
          }`}
          role="menubar"
        >
          <ul className="space-y-6" role="none">
            {has("dashboard") && (
              <li role="none">
                <SectionHeading label={t("sections.main")} />

                <ul className="space-y-1" role="none">
                  <li role="none">
                    <NavButton
                      icon={LayoutDashboard}
                      label={t("items.dashboard")}
                      page="dashboard"
                      onClick={() => handleNavigation("/dashboard")}
                    />
                  </li>
                </ul>
              </li>
            )}

            {/* A section with nothing in it renders nothing — no stray heading
                above an empty list for a tenant that has no operations modules. */}
            {operationsItems.length > 0 && (
              <li role="none">
                <SectionHeading label={t("sections.operations")} />

                <ul className="space-y-1" role="none">
                  {renderItems(operationsItems)}
                </ul>
              </li>
            )}

            {managementItems.length > 0 && (
              <li role="none">
                {(!isCollapsed || isMobile) && (
                  <div className="mb-3">
                    {/* Management Dropdown Header */}
                    <button
                      onClick={() => setIsManagementOpen(!isManagementOpen)}
                      className="flex items-center justify-between w-full px-3 py-2 text-[#8A9590] hover:text-[#E89D5C] hover:bg-white/5 rounded-lg transition-all duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <Building2 className="h-5 w-5 shrink-0" />
                        <span className="font-medium">
                          {t("sections.management")}
                        </span>
                      </div>
                      {isManagementOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}

                {(!isCollapsed || isMobile) && isManagementOpen && (
                  <ul className="space-y-1 ml-4" role="none">
                    {renderItems(managementItems)}
                  </ul>
                )}

                {isCollapsed && !isMobile && (
                  <div className="flex justify-center">
                    <button
                      onClick={onToggle}
                      className="flex items-center justify-center p-3 text-[#8A9590] hover:text-[#E89D5C] hover:bg-white/5 rounded-lg transition-all duration-200 group relative"
                      title={t("sections.management")}
                    >
                      <Building2 className="h-5 w-5 shrink-0" />
                      <div
                        className="
                      absolute left-full ml-2 px-2 py-1 bg-[#0B0F0E] border border-white/10 text-white text-sm rounded
                      opacity-0 group-hover:opacity-100 transition-opacity duration-200
                      pointer-events-none whitespace-nowrap z-50
                    "
                      >
                        {t("sections.management")}
                      </div>
                    </button>
                  </div>
                )}
              </li>
            )}

            {userManagementItems.length > 0 && (
              <li role="none">
                <SectionHeading label={t("sections.userManagement")} />

                <ul className="space-y-1" role="none">
                  {renderItems(userManagementItems)}
                </ul>
              </li>
            )}
          </ul>
        </nav>

        {has("helpCenter") && (
          <div className={`shrink-0 p-4 border-t border-white/8`}>
            <NavButton
              icon={MessageCircleQuestion}
              label={t("items.helpCentre")}
              page="help"
              onClick={() => handleNavigation("/help")}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
